import type { TaxonomyFieldValue } from '@/entities/taxonomy';

/** One row of the searchable editor. A null `value` is the Clear item. */
export interface ComboboxChoice {
  value: string | null;
  label: string;
}

/**
 * `Full value (CODE)`, so a picker shows the label beside what lands in the name. A code the value
 * already spells out, as `16:9` or `Brand [BR]`, would only repeat it.
 */
export function optionLabel({ value, code }: TaxonomyFieldValue): string {
  if (!code || code === value || value.includes(`[${code}]`) || value.includes(`(${code})`)) {
    return value;
  }
  return `${value} (${code})`;
}

/**
 * The rows of the searchable editor, each carrying the value it stands for.
 *
 * The label shows both halves so typing either the name or the code finds the row.
 * The VALUE is what travels back, and keeping the two separate is what fixes two
 * ways that matching by label broke: a vocabulary entry literally named "Clear"
 * wiped the cell, and `Foo (BAR)` collided with `Foo` carrying the code `BAR` so
 * picking the second committed the first. A null value is the Clear sentinel — the
 * same trick the short-list `Select` path uses, for the same reason.
 */
export function comboboxChoices(
  options: TaxonomyFieldValue[],
  value: string,
  clearLabel: string,
): ComboboxChoice[] {
  const choices: ComboboxChoice[] = options.map((option) => ({
    value: option.value,
    label: optionLabel(option),
  }));
  if (value) {
    choices.push({ value: null, label: clearLabel });
  }
  return choices;
}
